import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  listMailboxes,
  listMails,
  type MailListItem,
  type MailboxItem,
  type PaginationMeta,
} from '../api/client';
import MailDetailView from '../components/MailDetail';
import MailList from '../components/MailList';

export default function InboxPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [items, setItems] = useState<MailListItem[]>([]);
  const [pagination, setPagination] = useState<PaginationMeta | null>(null);
  const [mailboxes, setMailboxes] = useState<MailboxItem[]>([]);
  const [page, setPage] = useState(1);
  const [mailbox, setMailbox] = useState('');
  const [from, setFrom] = useState('');
  const [subject, setSubject] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listMails({
        page,
        pageSize: 20,
        mailbox: mailbox || undefined,
        from: from || undefined,
        subject: subject || undefined,
      });
      setItems(data.items);
      setPagination(data.pagination);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [page, mailbox, from, subject]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    listMailboxes()
      .then((d) => setMailboxes(d.items))
      .catch(() => setMailboxes([]));
  }, []);

  return (
    <div>
      <div className="toolbar">
        <select
          className="select"
          value={mailbox}
          onChange={(e) => {
            setPage(1);
            setMailbox(e.target.value);
          }}
        >
          <option value="">全部邮箱</option>
          {mailboxes.map((m) => (
            <option key={m.address} value={m.address}>
              {m.label ? `${m.label} <${m.address}>` : m.address}
            </option>
          ))}
        </select>
        <input
          className="input"
          placeholder="From 过滤"
          value={from}
          onChange={(e) => setFrom(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              setPage(1);
              void load();
            }
          }}
        />
        <input
          className="input"
          placeholder="Subject 过滤"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              setPage(1);
              void load();
            }
          }}
        />
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => {
            setPage(1);
            void load();
          }}
          disabled={loading}
        >
          查询
        </button>
        <button type="button" className="btn" onClick={() => void load()} disabled={loading}>
          刷新
        </button>
        {pagination && (
          <span className="muted" style={{ marginLeft: 'auto' }}>
            共 {pagination.total} 封 · 第 {pagination.page}/{pagination.totalPages} 页
          </span>
        )}
      </div>

      {error && <div className="error-banner">{error}</div>}

      <div className="split">
        <div className="panel" style={{ display: 'flex', flexDirection: 'column' }}>
          {loading && !items.length ? (
            <div className="empty">加载中…</div>
          ) : (
            <MailList items={items} activeId={id} basePath="/mail" />
          )}
          <div className="pagination">
            <button
              type="button"
              className="btn"
              disabled={!pagination || page <= 1 || loading}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              上一页
            </button>
            <span className="muted">{page}</span>
            <button
              type="button"
              className="btn"
              disabled={!pagination || page >= pagination.totalPages || loading}
              onClick={() => setPage((p) => p + 1)}
            >
              下一页
            </button>
          </div>
        </div>
        <MailDetailView
          mailId={id || null}
          onDeleted={(deletedId) => {
            setItems((prev) => prev.filter((x) => x.id !== deletedId));
            navigate('/');
            void load();
          }}
        />
      </div>
    </div>
  );
}
